import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Skeleton from '../ui/Skeleton';

const PAGE_SIZE = 50;
const SHORT_NARRATIVE_LIMIT = 80;

export async function fetchRoutingActionPage(runId, offset = 0, fetchImpl = fetch) {
  const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  const token = localStorage.getItem('token');
  const response = await fetchImpl(
    `/api/roteiro/${encodeURIComponent(runId)}/actions?${query}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(`Erro ${response.status} ao carregar histórico.`);
  return response.json();
}

function valueTransition(before, after) {
  return `${before ?? '—'} → ${after ?? '—'}`;
}

function operationSummary(value) {
  const parts = [];
  if (value?.machine) parts.push(`máquina ${value.machine}`);
  if (Number.isInteger(value?.position)) parts.push(`posição ${value.position}`);
  return parts.join(' · ');
}

export function describeRoutingAction(action) {
  const before = action.before_value;
  const after = action.after_value;
  switch (action.event_type) {
    case 'OPERATION_ADDED':
      return {
        title: `Operação ${after?.code ?? ''} adicionada`.replace('  ', ' '),
        detail: operationSummary(after),
      };
    case 'OPERATION_REMOVED':
      return {
        title: `Operação ${before?.code ?? ''} removida`.replace('  ', ' '),
        detail: operationSummary(before),
      };
    case 'OPERATION_MOVED':
      return { title: 'Operação movida', detail: `posição ${valueTransition(before?.position, after?.position)}` };
    case 'MACHINE_CHANGED':
      return { title: 'Máquina alterada', detail: valueTransition(before?.machine, after?.machine) };
    case 'NARRATIVE_CHANGED': {
      const long = before?.narrative_truncated || after?.narrative_truncated
        || (before?.narrative?.length ?? 0) > SHORT_NARRATIVE_LIMIT
        || (after?.narrative?.length ?? 0) > SHORT_NARRATIVE_LIMIT;
      return long
        ? { title: 'Narrativa longa alterada', detail: '' }
        : {
            title: 'Narrativa alterada',
            detail: `“${before?.narrative ?? ''}” → “${after?.narrative ?? ''}”`,
          };
    }
    case 'MOV_CHANGED':
      return { title: 'MOV alterado', detail: valueTransition(before?.mov || 'vazio', after?.mov || 'vazio') };
    case 'MULTIPLE_CHANGED':
      return { title: 'Múltiplo alterado', detail: valueTransition(before?.multiple, after?.multiple) };
    default:
      return { title: 'Alteração registrada', detail: '' };
  }
}

function formatDate(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function RoutingHistoryPanel({ runId, refreshKey = 0, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    setItems([]);
    setTotal(0);
    setLoading(true);
    setError(null);
    fetchRoutingActionPage(runId)
      .then((page) => {
        if (!active) return;
        setItems(page.items);
        setTotal(page.total);
      })
      .catch((err) => {
        if (!active) return;
        console.error('[RoutingHistoryPanel] Falha ao carregar histórico:', err);
        setError('Não foi possível carregar o histórico deste roteiro.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [runId, refreshKey, retryKey]);

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const groups = useMemo(() => {
    const byRevision = new Map();
    items.forEach((item) => {
      if (!byRevision.has(item.revision_number)) {
        byRevision.set(item.revision_number, {
          number: item.revision_number, date: item.created_at,
          author: item.user_display_name?.trim() || (item.user_id != null
            ? `Usuário #${item.user_id}` : 'Usuário indisponível'),
          events: [],
        });
      }
      byRevision.get(item.revision_number).events.push(item);
    });
    return [...byRevision.values()];
  }, [items]);

  const loadMore = async () => {
    if (loadingMore || items.length >= total) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await fetchRoutingActionPage(runId, items.length);
      setItems((previous) => [...previous, ...page.items]);
      setTotal(page.total);
    } catch (err) {
      console.error('[RoutingHistoryPanel] Falha ao carregar mais eventos:', err);
      setError('Não foi possível carregar mais eventos. Tente novamente.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Modal onClose={onClose} className="cw-routing-history-modal">
      <div className="cw-routing-history-head">
        <div>
          <h2>Histórico do Roteiro</h2>
          <p>Alterações salvas neste roteiro</p>
        </div>
        <Button variant="secondary" onClick={onClose} aria-label="Fechar histórico">Fechar</Button>
      </div>

      {loading && <div aria-label="Carregando histórico" className="cw-routing-history-loading">
        <Skeleton height="1.2em" width="35%" />
        <Skeleton height="1em" width="85%" />
        <Skeleton height="1em" width="70%" />
      </div>}

      {error && <div role="alert" className="cw-routing-history-error">
        {error}
        {!items.length && !loading && (
          <Button variant="secondary" onClick={() => setRetryKey((value) => value + 1)}>
            Tentar novamente
          </Button>
        )}
      </div>}

      {!loading && !error && items.length === 0 && (
        <p className="cw-routing-history-empty">
          Nenhuma alteração salva registrada para este roteiro.
        </p>
      )}

      {!loading && groups.map((group) => (
        <section className="cw-routing-history-revision" key={group.number}>
          <div className="cw-routing-history-revision-head">
            <h3>Revisão {group.number}</h3>
            <span className="cw-routing-history-revision-meta">
              <time dateTime={group.date}>{formatDate(group.date)}</time>
              <span aria-hidden="true"> · </span>{group.author}
            </span>
          </div>
          <ul className="cw-routing-history-events">
            {group.events.map((item) => {
              const description = describeRoutingAction(item);
              return <li key={item.id}>
                <strong>{description.title}</strong>
                {description.detail && <span>{description.detail}</span>}
              </li>;
            })}
          </ul>
        </section>
      ))}

      {!loading && items.length < total && (
        <div className="cw-routing-history-more">
          <Button variant="secondary" onClick={loadMore} loading={loadingMore}>
            Carregar mais
          </Button>
        </div>
      )}
    </Modal>
  );
}
